/** Inventory endpoints: copies, shelf locations, conditions, transactions. */

import apiBase, { BaseQueryParams, PaginatedResponse } from './api-base';

/**
 * Type definitions for Inventory module
 */
export interface ShelfLocation {
  id: number;
  /** First letter of each word in the library name, e.g. "SSL". Shared by that library's sections. */
  code: string;
  library: string;
  /** 1 to 3 capital letters or digits, e.g. "TRB". */
  section: string;
  /** "<code> <section>", e.g. "SSL TRB". */
  label: string;
  /** Every copy assigned here, including those out on loan. */
  copies_count?: number;
  created_at: string;
  updated_at: string;
}

/** The location on a copy (`location_info`). */
export type CopyLocation = Pick<ShelfLocation, 'id' | 'code' | 'library' | 'section' | 'label'>;

/** GET inventory/locations/summary/: totals for the staff Locations page. */
export interface LocationSummary {
  total: number;
  /** Distinct library names. */
  libraries: number;
  /** Copies with a location (withdrawn copies excluded). */
  shelved: number;
  /** Copies with no location (withdrawn copies excluded). */
  unshelved: number;
}

export interface Condition {
  id: number;
  name: string;
  name_display: string;
  description?: string;
  score: number;
  can_be_borrowed: boolean;
  requires_special_handling: boolean;
  book_count?: number;
  created_at: string;
  updated_at: string;
}

export interface BookCopy {
  id: number;
  book_info?: {
    id: number;
    title: string;
    isbn: string;
    authors: Array<{
      id: number;
      full_name: string;
    }>;
  };
  book_id?: number; // For create/update operations
  location_info?: CopyLocation | null;
  location_id?: number; // For create/update operations
  condition_info?: Condition;
  condition_id?: number; // For create/update operations
  barcode: string;
  accession_number: string;
  status: 'available' | 'borrowed' | 'maintenance' | 'missing' | 'withdrawn' | 'reserved' | 'in_transit';
  status_display: string;
  acquisition_date: string;
  acquisition_cost?: string;
  acquisition_source?: string;
  edition_notes?: string;
  total_circulations: number;
  last_borrowed_date?: string;
  is_reference_only: boolean;
  is_rare_book: boolean;
  requires_approval: boolean;
  internal_notes?: string;
  is_available_for_borrowing: boolean;
  display_location?: string;
  days_since_last_borrowed?: number;
  circulation_rate?: number;
  created_at: string;
  updated_at: string;
}

export interface InventoryTransaction {
  id: number;
  copy_info?: {
    id: number;
    book_info?: {
      id: number;
      title: string;
      isbn: string;
      authors: Array<{
        id: number;
        full_name: string;
      }>;
    };
    barcode: string;
    accession_number: string;
    status: string;
    status_display: string;
    condition_info?: Condition;
    display_location?: string;
    is_available_for_borrowing: boolean;
    total_circulations: number;
    acquisition_date: string;
  };
  copy_id?: number; // For create/update operations
  transaction_type: 'acquisition' | 'checkout' | 'checkin' | 'status_change' | 'location_change' | 'condition_change' | 'maintenance' | 'withdrawal' | 'audit';
  transaction_type_display: string;
  description: string;
  performed_by_info?: {
    id: number;
    username: string;
    full_name: string;
  };
  previous_status?: string;
  new_status?: string;
  previous_location_info?: CopyLocation | null;
  new_location_info?: CopyLocation | null;
  previous_condition_info?: Condition;
  new_condition_info?: Condition;
  transaction_date: string;
  notes?: string;
  is_automated: boolean;
}

export interface InventoryStats {
  total_copies: number;
  available_copies: number;
  borrowed_copies: number;
  maintenance_copies: number;
  overview?: {
    total_copies: number;
    available_copies: number;
    borrowed_copies: number;
    maintenance_copies: number;
  };
  status_breakdown: Array<{
    status: string;
    count: number;
  }>;
  condition_breakdown: Array<{
    condition__name: string;
    condition__score: number;
    count: number;
  }>;
  location_utilization: Array<{
    code: string;
    library: string;
    section: string;
    total_copies: number;
    available_copies: number;
  }>;
  recent_acquisitions: Array<{
    id: number;
    book__title: string;
    book__isbn: string;
    acquisition_date: string;
    acquisition_cost: string;
    accession_number: string;
  }>;
  popular_books: Array<{
    book__title: string;
    book__isbn: string;
    total_copies: number;
    total_circulations: number;
  }>;
}

/**
 * Query parameter types for different endpoints
 */
export interface ShelfLocationQueryParams extends BaseQueryParams {
  library?: string;
  library__icontains?: string;
  section?: string;
  code?: string;
}

export interface BookCopyQueryParams extends BaseQueryParams {
  book?: number;
  status?: string;
  condition?: number;
  location?: number;
  acquisition_date__gte?: string;
  acquisition_date__lte?: string;
  barcode?: string;
  accession_number?: string;
}

export interface TransactionQueryParams extends BaseQueryParams {
  book_copy?: number;
  transaction_type?: string;
  user?: number;
  timestamp__gte?: string;
  timestamp__lte?: string;
}

/**
 * Create/Update data types
 */
export interface CreateShelfLocationData {
  library: string;
  section: string;
}

export interface UpdateShelfLocationData extends Partial<CreateShelfLocationData> {
  id?: never; // Ensure id is not included in update data
}

export interface CreateConditionData {
  name: string;
  score: number;
  can_be_borrowed: boolean;
  requires_special_handling?: boolean;
  description?: string;
}

export interface UpdateConditionData extends Partial<CreateConditionData> {
  id?: never;
}

export interface CreateBookCopyData {
  book_id: number;
  barcode: string;
  accession_number: string;
  acquisition_date: string;
  status?: BookCopy['status'];
  acquisition_cost?: string;
  acquisition_source?: string;
  location_id?: number | null;
  condition_id: number;
  edition_notes?: string;
  is_reference_only?: boolean;
  is_rare_book?: boolean;
  requires_approval?: boolean;
  internal_notes?: string;
}

export interface UpdateBookCopyData extends Partial<CreateBookCopyData> {
  id?: never;
}

export interface BulkLocationUpdateData {
  copy_ids: number[];
  new_location_id: number;
  notes?: string;
}

export interface BulkStatusUpdateData {
  copy_ids: number[];
  new_status: BookCopy['status'];
  notes?: string;
}

/**
 * Shelf Location API methods
 */
export const shelfLocationsApi = {
  // List shelf locations with filtering and pagination
  getAll: (params?: ShelfLocationQueryParams) => 
    apiBase.get<PaginatedResponse<ShelfLocation>>('/inventory/locations/', { params }),

  // Get specific shelf location by ID
  getById: (id: number) => 
    apiBase.get<ShelfLocation>(`/inventory/locations/${id}/`),

  // Create new shelf location
  create: (data: CreateShelfLocationData) => 
    apiBase.post<ShelfLocation>('/inventory/locations/', data),

  // Update shelf location
  update: (id: number, data: UpdateShelfLocationData) => 
    apiBase.patch<ShelfLocation>(`/inventory/locations/${id}/`, data),

  // Delete shelf location
  delete: (id: number) => 
    apiBase.delete(`/inventory/locations/${id}/`),

  // Totals for the staff Locations page
  getSummary: () =>
    apiBase.get<LocationSummary>('/inventory/locations/summary/'),

  // Copies at a specific location
  getLocationBooks: (id: number, params?: BookCopyQueryParams) =>
    apiBase.get<PaginatedResponse<BookCopy>>(`/inventory/locations/${id}/books/`, { params }),
};

/**
 * Condition API methods
 */
export const conditionsApi = {
  // List all conditions (public endpoint)
  getAll: (params?: BaseQueryParams) => 
    apiBase.get<PaginatedResponse<Condition>>('/inventory/conditions/', { params }),

  // Get specific condition by ID
  getById: (id: number) => 
    apiBase.get<Condition>(`/inventory/conditions/${id}/`),

  // Create new condition (staff only)
  create: (data: CreateConditionData) => 
    apiBase.post<Condition>('/inventory/conditions/', data),

  // Update condition (staff only)
  update: (id: number, data: UpdateConditionData) => 
    apiBase.patch<Condition>(`/inventory/conditions/${id}/`, data),

  // Delete condition (staff only)
  delete: (id: number) => 
    apiBase.delete(`/inventory/conditions/${id}/`),

  // Get condition statistics
  getStatistics: () => 
    apiBase.get<{
      total_conditions: number;
      total_copies: number;
      avg_score: number;
      borrowable_conditions: number;
      condition_breakdown: Array<{
        name: string;
        copy_count: number;
        score: number;
        can_be_borrowed: boolean;
      }>;
    }>('/inventory/conditions/statistics/'),
};

/**
 * Book Copy API methods
 */
export const bookCopiesApi = {
  // List book copies with filtering and pagination
  getAll: (params?: BookCopyQueryParams) => 
    apiBase.get<PaginatedResponse<BookCopy>>('/inventory/copies/', { params }),

  // Get specific book copy by ID
  getById: (id: number) => 
    apiBase.get<BookCopy>(`/inventory/copies/${id}/`),

  // Create new book copy
  create: (data: CreateBookCopyData) => 
    apiBase.post<BookCopy>('/inventory/copies/', data),

  // Update book copy
  update: (id: number, data: UpdateBookCopyData) => 
    apiBase.patch<BookCopy>(`/inventory/copies/${id}/`, data),

  // Delete book copy (refused with 400 once the copy has loan history)
  delete: (id: number) => 
    apiBase.delete(`/inventory/copies/${id}/`),

  // Withdraw a copy from circulation, keeping its loan and fine history
  withdraw: (id: number, reason = '') =>
    apiBase.post<BookCopy>(`/inventory/copies/${id}/withdraw/`, { reason }),

  // Get inventory statistics
  getStatistics: () => 
    apiBase.get<InventoryStats>('/inventory/copies/statistics/'),

  // One more than the biggest number any accession number ends with (see lib/accession.ts)
  getNextAccessionNumber: () =>
    apiBase.get<{ next_number: number }>('/inventory/copies/next_accession/'),

  // Bulk update locations
  bulkLocationUpdate: (data: BulkLocationUpdateData) => 
    apiBase.post<{
      message: string;
      updated_count: number;
    }>('/inventory/copies/bulk_location_update/', data),

  // Bulk update statuses
  bulkStatusUpdate: (data: BulkStatusUpdateData) => 
    apiBase.post<{
      message: string;
      updated_count: number;
    }>('/inventory/copies/bulk_status_update/', data),

  // Get transaction history for a specific copy
  getTransactionHistory: (id: number, params?: TransactionQueryParams) =>
    apiBase.get<PaginatedResponse<InventoryTransaction>>(`/inventory/copies/${id}/transaction_history/`, { params }),

  // Look up a copy by barcode: used for quick issue/return workflows
  getByBarcode: (barcode: string) =>
    apiBase.get(`/inventory/copies/by-barcode/${barcode}/`),
};

/**
 * Inventory Transaction API methods
 */
export const inventoryTransactionsApi = {
  // List inventory transactions (read-only)
  getAll: (params?: TransactionQueryParams) => 
    apiBase.get<PaginatedResponse<InventoryTransaction>>('/inventory/transactions/', { params }),

  // Get specific transaction by ID
  getById: (id: number) => 
    apiBase.get<InventoryTransaction>(`/inventory/transactions/${id}/`),

  // Get activity summary
  getActivitySummary: (params?: { days?: number }) => 
    apiBase.get<{
      today: number;
      this_week: number;
      this_month: number;
      transaction_types: Record<string, number>;
      most_active_users: Array<{
        username: string;
        transaction_count: number;
      }>;
    }>('/inventory/transactions/activity_summary/', { params }),
};

/**
 * Combined inventory API object for easy importing
 */
export const inventoryApi = {
  shelfLocations: shelfLocationsApi,
  conditions: conditionsApi,
  bookCopies: bookCopiesApi,
  transactions: inventoryTransactionsApi,
};

export default inventoryApi;
